import type { ConversationDto, RewindResultDto } from '@dbrain/shared';
import { Badge, Button, IconButton, Icons, Menu, MenuItem, MenuLabel, MenuSeparator, Select, TextArea, Dialog } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useFeedback, useModels } from '../../api/hooks.js';
import type { ChatItem } from '@dbrain/app-core/conversation-state';
import { changeOf, diffLines } from '@dbrain/app-core/diff';
import { modelName } from '@dbrain/app-core/model-tasks';
import { SLASH_COMMANDS, type SlashCommand, type SlashEntry } from '@dbrain/app-core/slash';
import { ErrorAlert } from '../ErrorAlert.js';
import { ChatModelMenu, useChatModels } from './ChatModelMenu.js';
import { CopyButton, Markdown } from './Markdown.js';

/**
 * Session parts of a conversation (Phase 10): the model of this conversation (D-27; the context dialog is
 * in ContextPopover.tsx), rewind (D-22), edit and
 * regenerate (D-40), answer feedback (SA-064), side questions (QW-112,
 * D-17), the slash menu (QW-113, QW-1211), and the lines a compaction, a
 * branch or a saved memory leaves in the conversation.
 */
const number = (value: number, locale: string) => value.toLocaleString(locale);

// Model of this conversation (D-27) -------------------------------------------------------------

export function ModelPicker({ conversation, open }: { conversation: ConversationDto; open?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const models = useModels();
  const select = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (open) select.current?.focus();
  }, [open]);
  const change = useMutation({
    mutationFn: (body: { modelRef?: string | null; thinking?: 'auto' | 'off' }) => api<ConversationDto>('PATCH', `/api/conversations/${conversation.id}`, body),
    onSuccess: (data) => queryClient.setQueryData(keys.conversation(conversation.id), data),
  });
  // D-27: this conversation's model becomes the user's default for its experience
  const task = conversation.profile === 'code' ? 'code' : 'main';
  const makeDefault = useMutation({
    mutationFn: () => api('PUT', `/api/model-routes/${task}`, { scope: 'user', modelRef: conversation.modelRef }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.models });
      void queryClient.invalidateQueries({ queryKey: keys.routes });
    },
  });
  // MM-006, 16c.1: only the models this person may pick in Code, and this conversation's own when it is no longer one of them
  const codeRefs = models.data?.code?.modelRefs;
  const options = (models.data?.models ?? []).filter((m) => m.kind === 'chat' && m.enabled && m.availability.ok && ((codeRefs ? codeRefs.includes(m.id) : m.allowed) || m.id === conversation.modelRef));
  const fallback = models.data?.routes[task]?.modelRef ?? models.data?.routes.main?.modelRef;
  const fallbackModel = options.find((m) => m.id === fallback);
  const isDefault = conversation.modelRef !== null && conversation.modelRef === fallback;
  // the name from the settings as in the bar and the mockups ("Varsayılan (Qwen3.8 27B)"); the id in the tooltip
  const nameOf = (m: (typeof options)[number]) => modelName(m);
  const current = options.find((m) => m.id === (conversation.modelRef ?? fallback));
  const full = current ? `${current.modelId} · ${conversation.modelRef === null || conversation.modelRef === fallback ? t('model.isDefaultLower') : current.providerName}` : t('model.label');
  return (
    <span className="model-picker">
      <label>
        <span className="ui-visually-hidden">{t('model.label')}</span>
        <Select
          ref={select}
          size="sm"
          value={conversation.modelRef ?? ''}
          onChange={(e) => change.mutate({ modelRef: e.target.value || null })}
          data-testid="model-picker"
          title={`${full}\n${t('model.hint')}`}
        >
          <option value="" title={fallbackModel?.modelId}>
            {t('model.default', { name: fallbackModel ? nameOf(fallbackModel) : '—' })}
          </option>
          {options.map((m) => {
            const name = nameOf(m);
            // the same model (or name) served by two providers: the provider tells them apart
            const twin = options.some((o) => o.id !== m.id && (nameOf(o) === name || o.modelId === m.modelId));
            return (
              <option key={m.id} value={m.id} disabled={!m.allowed} title={m.modelId}>
                {twin ? `${name} · ${m.providerName}` : name}
                {m.allowed ? '' : ` (${t('model.notAllowed')})`}
              </option>
            );
          })}
        </Select>
      </label>
      {conversation.modelRef && !isDefault ? (
        <Button size="sm" variant="ghost" busy={makeDefault.isPending} onClick={() => makeDefault.mutate()} data-testid="model-make-default">
          {makeDefault.isSuccess ? t('model.isDefault') : t('model.makeDefault')}
        </Button>
      ) : null}
      <ErrorAlert error={change.error ?? makeDefault.error} />
    </span>
  );
}

/**
 * The model of a conversation in its bar (D-27, MM-006; docs/design/mockups/SoruKarti.dc.html): a green dot and the
 * short name, the full name in the tooltip; the menu lists the models this person may pick (16c.1, B2cModelSecici:
 * on Ücretsiz Pro's models with a lock) and can make this one their default. `/model` opens it.
 */
export function ModelMenu({ conversation, open }: { conversation: ConversationDto; open?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (modelRef: string | null) => api<ConversationDto>('PATCH', `/api/conversations/${conversation.id}`, { modelRef }),
    onSuccess: (data) => queryClient.setQueryData(keys.conversation(conversation.id), data),
  });
  const task = conversation.profile === 'code' ? 'code' : 'main';
  const { fallback } = useChatModels(task);
  const makeDefault = useMutation({
    mutationFn: () => api('PUT', `/api/model-routes/${task}`, { scope: 'user', modelRef: conversation.modelRef }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.models });
      void queryClient.invalidateQueries({ queryKey: keys.routes });
    },
  });
  const isDefault = conversation.modelRef === null || conversation.modelRef === fallback;
  return (
    <>
      <ChatModelMenu
        value={conversation.modelRef}
        onPick={(ref) => change.mutate(ref)}
        task={task}
        variant="bar"
        {...(open !== undefined ? { open } : {})}
        data-testid="model-menu"
        extra={
          conversation.modelRef && !isDefault ? (
            <>
              <MenuSeparator />
              <MenuItem icon={<Icons.pin />} disabled={makeDefault.isPending} onClick={() => makeDefault.mutate()} data-testid="model-make-default">
                {makeDefault.isSuccess ? t('model.isDefault') : t('model.makeDefault')}
              </MenuItem>
            </>
          ) : null
        }
      />
      <ErrorAlert error={change.error ?? makeDefault.error} />
    </>
  );
}

// Rewind (D-22) ---------------------------------------------------------------------------------

interface RewindPreview {
  fromSeq: number;
  messages: number;
  files: { available: boolean; changes: { status: string; path: string }[] } | null;
}

export function RewindDialog({
  conversation,
  points,
  initialSeq,
  onClose,
  onDone,
}: {
  conversation: ConversationDto;
  points: Extract<ChatItem, { kind: 'user' }>[];
  initialSeq: number | null;
  onClose: () => void;
  onDone: (result: RewindResultDto) => void;
}) {
  const { t, i18n } = useTranslation();
  const [seq, setSeq] = useState<number | null>(initialSeq ?? points.at(-1)?.seq ?? null);
  const [files, setFiles] = useState(false);
  const preview = useQuery({
    queryKey: ['rewind', conversation.id, seq],
    queryFn: () => api<RewindPreview>('GET', `/api/conversations/${conversation.id}/rewind/${seq}`),
    enabled: seq !== null,
  });
  const rewind = useMutation({
    mutationFn: () => api<RewindResultDto>('POST', `/api/conversations/${conversation.id}/rewind`, { toSeq: seq, files }),
    onSuccess: onDone,
  });
  return (
    <Dialog title={t('rewind.title')} closeLabel={t('common.close')} onClose={onClose} data-testid="rewind-dialog">
      <p className="muted">{t('rewind.hint')}</p>
      {points.length === 0 ? <p>{t('rewind.none')}</p> : null}
      <ol className="rewind-points">
        {points.map((point) => (
          <li key={point.seq}>
            <label className={seq === point.seq ? 'rewind-point rewind-point--active' : 'rewind-point'}>
              <input type="radio" name="rewind-point" checked={seq === point.seq} onChange={() => setSeq(point.seq)} data-testid="rewind-point" />
              <span className="rewind-point__text">{point.text.slice(0, 160) || t('rewind.attachmentsOnly')}</span>
              <span className="muted rewind-point__time">{new Date(point.createdAt).toLocaleString(i18n.language)}</span>
            </label>
          </li>
        ))}
      </ol>
      {preview.data ? (
        <p data-testid="rewind-summary">{t('rewind.removes', { count: preview.data.messages })}</p>
      ) : null}
      {conversation.profile === 'code' && preview.data?.files ? (
        <fieldset className="rewind-files">
          <legend>{t('rewind.what')}</legend>
          <label>
            <input type="radio" name="rewind-files" checked={!files} onChange={() => setFiles(false)} /> {t('rewind.conversationOnly')}
          </label>
          <label>
            <input type="radio" name="rewind-files" checked={files} onChange={() => setFiles(true)} disabled={!preview.data.files.available} data-testid="rewind-with-files" />{' '}
            {t('rewind.withFiles')}
          </label>
          {files ? (
            preview.data.files.changes.length === 0 ? (
              <p className="muted">{t('rewind.noFileChanges')}</p>
            ) : (
              <ul className="rewind-changes" data-testid="rewind-changes">
                {preview.data.files.changes.slice(0, 50).map((change) => (
                  <li key={change.path}>
                    <Badge tone={change.status === 'added' ? 'success' : change.status === 'deleted' ? 'warning' : 'accent'}>{t(`rewind.status.${change.status}`)}</Badge> {change.path}
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </fieldset>
      ) : null}
      <ErrorAlert error={preview.error ?? rewind.error} />
      <div className="row dialog__actions">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button busy={rewind.isPending} disabled={seq === null} onClick={() => rewind.mutate()} data-testid="rewind-confirm">
          {t('rewind.confirm')}
        </Button>
      </div>
    </Dialog>
  );
}

// Messages ---------------------------------------------------------------------------------------

/** A message's edit and rewind; while a turn runs they wait (disabled) and keep their place. */
export function UserMessageActions({ onEdit, onRewind, disabled = false }: { onEdit?: () => void; onRewind?: () => void; disabled?: boolean }) {
  const { t } = useTranslation();
  if (!onEdit && !onRewind) return null;
  return (
    <div className="message__actions">
      {onEdit ? <IconButton size="sm" label={t('message.edit')} icon={<Icons.edit size={16} />} onClick={onEdit} disabled={disabled} data-testid="message-edit" /> : null}
      {onRewind ? <IconButton size="sm" label={t('message.rewind')} icon={<Icons.undo size={16} />} onClick={onRewind} disabled={disabled} data-testid="message-rewind" /> : null}
    </div>
  );
}

export function EditMessage({ conversationId, item, onClose }: { conversationId: string; item: Extract<ChatItem, { kind: 'user' }>; onClose: () => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(item.text);
  const edit = useMutation({
    mutationFn: () => api('POST', `/api/conversations/${conversationId}/messages/${item.seq}/edit`, { text: text.trim() }),
    onSuccess: onClose,
  });
  return (
    <form
      className="message message--user message--editing"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) edit.mutate();
      }}
      data-testid="message-editor"
    >
      <TextArea value={text} onChange={(e) => setText(e.target.value)} aria-label={t('message.edit')} autoFocus />
      <p className="muted">{t('message.editHint')}</p>
      <ErrorAlert error={edit.error} />
      <div className="row">
        <Button size="sm" type="submit" busy={edit.isPending} disabled={!text.trim()} data-testid="message-edit-save">
          {t('message.editSend')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

/** SA-064: thumbs up or down, a note on the way down; D-40: the last answer again, from another model if wanted. */
/** Copy, feedback and regenerate under an answer; the row stays while a later turn runs, regenerating waits. */
export function AnswerActions({ conversation, item, last, running = false }: { conversation: ConversationDto; item: Extract<ChatItem, { kind: 'assistant' }>; last: boolean; running?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const feedback = useFeedback(conversation.id);
  const models = useModels();
  const [note, setNote] = useState<string | null>(null);
  const rating = feedback.data?.find((f) => f.messageId === item.messageId)?.rating ?? 0;
  const rate = useMutation({
    mutationFn: (body: { rating: 1 | -1; note?: string }) => api('PUT', `/api/conversations/${conversation.id}/feedback`, { messageId: item.messageId, ...body }),
    onSuccess: () => {
      setNote(null);
      void queryClient.invalidateQueries({ queryKey: keys.feedback(conversation.id) });
    },
  });
  const regenerate = useMutation({
    mutationFn: (modelRef: string | undefined) => api('POST', `/api/conversations/${conversation.id}/regenerate`, modelRef ? { modelRef } : {}),
  });
  const options = (models.data?.models ?? []).filter((m) => m.kind === 'chat' && m.enabled && m.availability.ok && m.allowed);
  return (
    <div className="answer-actions" data-testid="answer-actions">
      <CopyButton text={item.text} iconOnly />
      <IconButton
        size="sm"
        aria-pressed={rating === 1}
        label={t('message.good')}
        icon={<Icons.thumbsUp size={16} />}
        onClick={() => rate.mutate({ rating: 1 })}
        data-testid="feedback-up"
      />
      <IconButton size="sm" aria-pressed={rating === -1} label={t('message.bad')} icon={<Icons.thumbsDown size={16} />} onClick={() => setNote('')} data-testid="feedback-down" />
      {last ? (
        <Menu
          className="regenerate"
          label={t('message.regenerate')}
          triggerClassName="ui-icon-button ui-icon-button--sm"
          trigger={regenerate.isPending ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : <Icons.regenerate size={16} />}
          panelClassName="regenerate-panel"
          data-testid="regenerate"
        >
          <MenuLabel>{t('message.regenerateWith')}</MenuLabel>
          <MenuItem icon={<Icons.regenerate />} disabled={running} onClick={() => regenerate.mutate(undefined)} data-testid="regenerate-same">
            {t('message.sameModel')}
          </MenuItem>
          {options.map((m) => (
            <MenuItem key={m.id} icon={<Icons.models />} disabled={running} onClick={() => regenerate.mutate(m.id)} data-testid="regenerate-model">
              {m.displayName ?? m.modelId}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
      {note !== null ? (
        <form
          className="feedback-note"
          onSubmit={(e) => {
            e.preventDefault();
            rate.mutate({ rating: -1, ...(note.trim() ? { note: note.trim() } : {}) });
          }}
        >
          <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('message.badNote')} aria-label={t('message.badNote')} data-testid="feedback-note" />
          <div className="row">
            <Button size="sm" type="submit" busy={rate.isPending} data-testid="feedback-send">
              {t('message.sendFeedback')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNote(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
      <ErrorAlert error={rate.error ?? regenerate.error} />
    </div>
  );
}

// Lines in the conversation ---------------------------------------------------------------------

export function CompactionLine({ item }: { item: Extract<ChatItem, { kind: 'compaction' }> }) {
  const { t, i18n } = useTranslation();
  return (
    <details className="divider-line compaction-line" data-testid="compaction-line" data-reason={item.reason}>
      <summary>
        {t(`compaction.${item.reason === 'manual' ? 'manual' : 'auto'}`)}
        {item.tokensBefore !== null && item.tokensAfter !== null
          ? ` · ${t('compaction.tokens', { before: number(item.tokensBefore, i18n.language), after: number(item.tokensAfter, i18n.language) })}`
          : ''}
      </summary>
      <Markdown source={item.summary} className="compaction-line__summary" />
    </details>
  );
}

export function MemoryLine({ item }: { item: Extract<ChatItem, { kind: 'memory' }> }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/api/memories/${item.memoryId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.memories }),
  });
  return (
    <p className="divider-line memory-line" data-testid="memory-line">
      {remove.isSuccess ? t('memoryLine.removed') : t('memoryLine.saved', { content: item.content })}{' '}
      {!remove.isSuccess ? (
        <>
          <Link to="/memory">{t('memoryLine.open')}</Link>{' '}
          <Button size="sm" variant="ghost" busy={remove.isPending} onClick={() => remove.mutate()} data-testid="memory-undo">
            {t('memoryLine.undo')}
          </Button>
        </>
      ) : null}
      <ErrorAlert error={remove.error} />
    </p>
  );
}

export function ForkedLine({ item }: { item: Extract<ChatItem, { kind: 'forked' }> }) {
  const { t } = useTranslation();
  return (
    <p className="divider-line" data-testid="forked-line">
      {t('branch.from')} <Link to={`/c/${item.fromConversationId}`}>{item.title ?? t('conversation.untitled')}</Link>
    </p>
  );
}

// Edits as a diff ----------------------------------------------------------------------------------

/** Lines added and removed by an edit or a write, for its card. */
export function changeStats(tool: string, args: string): { added: number; removed: number } | null {
  const change = changeOf(tool, args);
  if (!change) return null;
  const lines = diffLines(change.before, change.after);
  return { added: lines.filter((l) => l.kind === 'add').length, removed: lines.filter((l) => l.kind === 'del').length };
}

export function DiffView({ tool, args }: { tool: string; args: string }) {
  const { t } = useTranslation();
  const change = changeOf(tool, args);
  if (!change) return null;
  const lines = diffLines(change.before, change.after);
  const shown = lines.slice(0, 400);
  return (
    <div className="diff" data-testid="diff-view">
      <div className="diff__head muted">
        {change.path} · +{lines.filter((l) => l.kind === 'add').length} −{lines.filter((l) => l.kind === 'del').length}
      </div>
      <pre className="diff__body">
        {shown.map((line, index) => (
          <span key={index} className={`diff__line diff__line--${line.kind}`}>
            {line.kind === 'add' ? '+ ' : line.kind === 'del' ? '- ' : '  '}
            {line.text}
            {'\n'}
          </span>
        ))}
      </pre>
      {lines.length > shown.length ? <p className="muted">{t('diff.more', { count: lines.length - shown.length })}</p> : null}
    </div>
  );
}

// Side question (QW-112, D-17) ----------------------------------------------------------------------

export interface SideAnswer {
  id: number;
  question: string;
  answer: string | null;
  error: unknown;
}

export function SideQuestions({ entries, onClose }: { entries: SideAnswer[]; onClose: () => void }) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  return (
    <section className="side-questions" aria-label={t('side.title')} data-testid="side-questions">
      <div className="side-questions__head">
        <strong>{t('side.title')}</strong>
        <span className="muted">{t('side.hint')}</span>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="side-close">
          {t('common.close')}
        </Button>
      </div>
      {entries.map((entry) => (
        <div key={entry.id} className="side-question" data-testid="side-question">
          <p className="side-question__q">{entry.question}</p>
          {entry.answer !== null ? <Markdown source={entry.answer} /> : entry.error ? <ErrorAlert error={entry.error} /> : <p className="muted">{t('side.thinking')}</p>}
        </div>
      ))}
    </section>
  );
}

// Slash menu (QW-113, QW-1211) -------------------------------------------------------------------------

export function SlashMenu({ entries, active, onPick }: { entries: SlashEntry[]; active: number; onPick: (entry: SlashEntry) => void }) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  return (
    <ul className="slash-menu" role="listbox" aria-label={t('slash.title')} data-testid="slash-menu">
      {entries.map((entry, index) => (
        <li key={`${entry.kind}-${entry.name}`} role="option" aria-selected={index === active}>
          <button
            type="button"
            className={index === active ? 'slash-menu__item slash-menu__item--active' : 'slash-menu__item'}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(entry);
            }}
            data-testid={entry.kind === 'skill' ? 'slash-skill' : 'slash-item'}
            data-skill={entry.kind === 'skill' ? entry.name : undefined}
            data-command={entry.kind === 'command' ? entry.name : undefined}
          >
            <code>/{entry.name}</code>{' '}
            <span className="muted">{entry.kind === 'skill' ? `${t('slash.skill')} · ${entry.description}` : t(`slash.commands.${entry.name}`)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function SlashHelp({ onClose, commands = SLASH_COMMANDS }: { onClose: () => void; commands?: readonly SlashCommand[] }) {
  const { t } = useTranslation();
  return (
    <section className="side-questions" data-testid="slash-help">
      <div className="side-questions__head">
        <strong>{t('slash.title')}</strong>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
      <dl className="slash-help">
        {commands.map((command) => (
          <div key={command}>
            <dt>
              <code>/{command}</code>
            </dt>
            <dd>{t(`slash.commands.${command}`)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// Recap (QW-212) ---------------------------------------------------------------------------------------

export function RecapPanel({ text, error, pending, onClose }: { text: string | null; error: unknown; pending: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!text && !error && !pending) return null;
  return (
    <section className="recap" data-testid="recap">
      <div className="side-questions__head">
        <strong>{t('recap.title')}</strong>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
      {pending ? <p className="muted">{t('recap.pending')}</p> : null}
      {text ? <Markdown source={text} /> : null}
      <ErrorAlert error={error} />
    </section>
  );
}
